import React, { useCallback, useMemo, useRef, useState } from "react";

/**Third Part Libraries */
import { useTranslation } from "react-i18next";

/**Custom Components**/
import LoadingSpinner from "../../utils/components/common/loadingSpinner/LoadingSpinner";
import SearchBox from "../../utils/components/common/searchBox/deviceSearchBox";
import Icons from "../../utils/components/icons";
import DeviceInfo from "./DeviceInfo";
import DeviceSetup from "./DeviceSetup";
import Diagnostics from "./Diagnostics";
import FaultConfiguration from "./FaultConfiguration";
import Logs from "./Logs";
import EmptyScreen from "../../utils/components/common/EmptyScreen/EmptyScreen";

/**Services and generic functions**/
import { serviceToolTabKeys } from "../../utils/constants/serviceToolConstant";

/**Style**/
import "./ServiceTool.scss";
import { useLocation } from "react-router-dom";

// Service tool Tabs array
const { deviceInfo, deviceSetup, diagnostics, faultConfiguration, logs } =
  serviceToolTabKeys;
const serviceTabs = [
  {
    key: deviceInfo,
    label: "Device Info",
    icon: Icons.deviceInfo,
  },
  {
    key: deviceSetup,
    label: "Device Setup",
    icon: Icons.deviceSetup,
  },
  {
    key: diagnostics,
    label: "Diagnostics",
    icon: Icons.diagnostics,
  },
  {
    key: faultConfiguration,
    label: "Fault Configuration",
    icon: Icons.faultConfiguration,
  },
  { key: logs, label: "Logs", icon: Icons.logs },
];

const ServiceTool = () => {
  const { t } = useTranslation("serviceTool");
  const searchBoxRef = useRef();
  const location = useLocation();
  const locationStateData = location?.state || null; // Check if it is redirected from Device-detail-page, to pre-select the device
  const [selectedDeviceDetails, setSelectedDeviceDetails] = useState();
  const [selectedDevice, setSelectedDevice] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(serviceToolTabKeys.deviceInfo);

  /**Update the filterState for device */
  const updateSelectedDevice = useCallback(() => {
    const deviceList = searchBoxRef.current.getSelectedList();
    const deviceIdList = deviceList.map((list) => {
      const id = list.PK.slice(2);
      return id;
    });
    setSelectedDeviceDetails(deviceList);
    setSelectedDevice(deviceIdList.length ? deviceIdList : undefined); //Add above deviceIdList once search filter code is done
  }, []);

  const renderComponent = useCallback(
    (currentKey) => {
      switch (currentKey) {
        case deviceInfo:
          return (
            <DeviceInfo
              selectedDeviceDetails={selectedDeviceDetails}
              setIsLoading={(status) => setIsLoading(status)}
            />
          );
        case deviceSetup:
          return <DeviceSetup selectedDevice={selectedDevice} />;
        case diagnostics:
          return <Diagnostics />;
        case faultConfiguration:
          return <FaultConfiguration />;
        case logs:
          return (
            <Logs selectedDevice={selectedDevice} appLoading={isLoading} />
          );
        default:
      }
    },
    [isLoading, selectedDevice, selectedDeviceDetails]
  );

  // Device is selected or not
  const isDeviceSelected = useMemo(
    () =>
      selectedDevice !== undefined && selectedDevice.length ? true : false,
    [selectedDevice]
  );

  return (
    <>
      {/* On scroll to hide white rounded corner start */}
      <div className="horizontal-bar" />
      {/* On scroll to hide white rounded corner end */}

      <div className="bodyInner service-tool-page">
        <div className="bodyContainer">
          <div className="card service-tool-section">
            <div className="make-sticky-sub-header" id="sticky-sub-header">
              {/* Select a device header start */}
              <div className="sub-header-section">
                <p class="heading-h1 sub-label">{t("selectDevice")} </p>
                <div className="service-tool-device-dropdown">
                  <SearchBox
                    ref={searchBoxRef}
                    autoid="service-tool-search-device"
                    placeholderText={t("searchDevicePlaceholder")}
                    titleText={t("selectDevicePlaceholder")}
                    getOptionCallBack={() => {
                      updateSelectedDevice();
                    }}
                    defaultSelectedVal={locationStateData?.devicePK || null}
                    callFrom="service-tool"
                  />
                </div>
                <div className="session-button">
                  <span className="text-h1">{t("startSession")}</span>
                </div>
              </div>
              {/* Select a device header end */}

              {/* Tab section start */}
              <div className="service-tab-section">
                {serviceTabs?.map((item) => {
                  return (
                    <div
                      className={`tab-item ${item.key} ${
                        currentTab === item.key ? "active-tab" : ""
                      }`}
                      onClick={() => {
                        setCurrentTab(item.key);
                      }}
                    >
                      <span>{item.icon}</span>
                      <h1 key={item.key} className="heading-h3">
                        {item.label}
                      </h1>
                    </div>
                  );
                })}
              </div>
              {/* Tab section end */}
            </div>

            {/* Comonent render by selected tab start */}
            <div className="service-component-by-tab">
              {isDeviceSelected ? (
                renderComponent(currentTab)
              ) : (
                <div className="empty-screen-box">
                  <EmptyScreen
                    title={t("noDevice")}
                    img={Icons.noDeviceOnboarded}
                    desc={t("noDeviceDesc")}
                  />
                </div>
              )}
            </div>
            {/* Comonent render by selected tab end */}
          </div>
          {isLoading && <LoadingSpinner />}
        </div>
      </div>
    </>
  );
};

export default ServiceTool;
